import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import { formatMoney, formatDate, today, primero } from '../finanzas-cxp/shared.js';

/**
 * Captura de Abono — réplica fiel de `IbuFront/src/components/views/Admin/Abonos/AbonosCreate.jsx`
 * (V1), NO una simplificación. Flujo real de V1 (confirmado leyendo el JSX):
 *   1. Selecciona Cliente → carga sus Cargos y CargosContenedor CON SALDO PENDIENTE
 *      (`GET /cargos/cliente/:id/por-pagar`, `GET /cargos-contenedor/cliente/:id/por-pagar`).
 *   2. Cada renglón tiene un checkbox + un importe editable (tope: el saldo del renglón).
 *   3. Al guardar, POR CADA renglón seleccionado (multi-cargo, no un solo documento):
 *      `POST /abonos` (un Abono por cargo, referenciando `cargos:[id]` o
 *      `cargosContenedor:[id]`) + `PATCH /cargos(-contenedor)/:id/pagar` con el monto
 *      abonado — exactamente la pareja de llamadas que hace V1 (el backend NO
 *      actualiza el cargo desde el endpoint de Abonos, es responsabilidad del
 *      frontend encadenar ambas, igual que V1).
 * DUDAS.md D-11.9 (versión anterior de esta página, "alta simplificada") queda
 * SUPERADA por esta reconstrucción completa (2026-09-28, instrucción explícita del
 * dueño del negocio: "tienes que hacer todo como V1").
 */
export default function AbonosCapturaPage() {
  const { user, hasPermiso } = useContext(AuthContext);
  const puedeCrear = hasPermiso('abonos:crear');

  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState('');
  const [fecha, setFecha] = useState(today());
  const [formaPago, setFormaPago] = useState('Efectivo');
  const [concepto, setConcepto] = useState('Abono');
  const [bancos, setBancos] = useState([]);
  const [selectedBanco, setSelectedBanco] = useState('');
  const [renglones, setRenglones] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    apiClient.get('/clientes', { params: { limit: 500, sortBy: 'nombre_comercial', sortOrder: 'asc' } })
      .then(({ data }) => setClientes(data.data || []));
    apiClient.get('/bancos', { params: { limit: 200 } })
      .then(({ data }) => setBancos(data.data || []));
  }, []);

  useEffect(() => {
    if (!selectedCliente) { setRenglones([]); return; }
    Promise.all([
      apiClient.get(`/cargos-contenedor/cliente/${selectedCliente}/por-pagar`),
      apiClient.get(`/cargos/cliente/${selectedCliente}/por-pagar`),
    ]).then(([rCC, rC]) => {
      const cc = (rCC.data.data || []).map((a) => ({
        id: a._id, tipo: 'cargoContenedor',
        fecha: a.fecha, nota: a.idCargoContenedor,
        total: a.total, comision: 0, abonos: a.abonos, saldo: a.saldo,
        importe: a.saldo, observaciones: 'NA', select: false,
      }));
      const c = (rC.data.data || []).map((a) => ({
        id: a._id, tipo: 'cargo',
        fecha: a.fecha, nota: a.idCargo,
        total: a.total, comision: a.comisionNumero || 0, abonos: a.abonos, saldo: a.saldo,
        importe: a.saldo, observaciones: 'NA', select: false,
      }));
      setRenglones([...cc, ...c]);
    });
  }, [selectedCliente]);

  const total = useMemo(
    () => renglones.filter((r) => r.select).reduce((t, r) => t + (parseFloat(r.importe) || 0), 0),
    [renglones]
  );

  function actualizarRenglon(id, cambios) {
    setRenglones((prev) => prev.map((r) => (r.id === id ? { ...r, ...cambios } : r)));
  }

  function cambiarImporte(id, valor) {
    const renglon = renglones.find((r) => r.id === id);
    if (!renglon) return;
    const num = parseFloat(valor) || 0;
    if (num > renglon.saldo) {
      Swal.fire({ icon: 'error', title: 'El pago excede el saldo' });
      return;
    }
    actualizarRenglon(id, { importe: num });
  }

  async function guardar() {
    if (!selectedCliente) return Swal.fire({ icon: 'error', title: 'Selecciona un cliente' });
    if (!selectedBanco) return Swal.fire({ icon: 'error', title: 'Debe seleccionar un Banco' });
    const seleccionados = renglones.filter((r) => r.select && r.importe > 0);
    if (!seleccionados.length) return Swal.fire({ icon: 'error', title: 'Selecciona al menos un cargo' });

    const confirmacion = await Swal.fire({
      title: '¿Estás seguro?', text: 'Se registrará el abono', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Sí, enviar', confirmButtonColor: '#3085d6',
    });
    if (!confirmacion.isConfirmed) return;

    setGuardando(true);
    try {
      for (const r of seleccionados) {
        const esContenedor = r.tipo === 'cargoContenedor';
        await apiClient.post('/abonos', {
          fecha, importe: r.importe, clientes: selectedCliente,
          ...(esContenedor ? { cargosContenedor: [r.id] } : { cargos: [r.id] }),
          formaPago, concepto, observaciones: r.observaciones, bancos: selectedBanco,
        });
        await apiClient.patch(`/${esContenedor ? 'cargos-contenedor' : 'cargos'}/${r.id}/pagar`, { saldo: r.importe });
      }
      Swal.fire({ icon: 'success', title: 'Creado con éxito', timer: 1500, showConfirmButton: false });
      setSelectedCliente('');
      setRenglones([]);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'No se pudo registrar el abono', text: error.message });
    } finally {
      setGuardando(false);
    }
  }

  if (!puedeCrear) return <div className="text-slate-500">No tienes permiso para capturar abonos.</div>;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-slate-900">Captura un Pago</h1>

      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 items-end">
        <div>
          <label className="label">Fecha</label>
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Cliente</label>
          <select value={selectedCliente} onChange={(e) => setSelectedCliente(e.target.value)}
            className="select-base">
            <option value="">Selecciona</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nombre_comercial}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Concepto</label>
          <select value={concepto} onChange={(e) => setConcepto(e.target.value)}
            className="select-base">
            <option value="">Selecciona un Concepto</option>
            <option value="Abono">Abono</option>
            <option value="Bonificacion">Bonificacion</option>
          </select>
        </div>
        <div>
          <label className="label">Forma de Pago</label>
          <select value={formaPago} onChange={(e) => setFormaPago(e.target.value)}
            className="select-base">
            <option value="">Selecciona una Forma de Pago</option>
            <option value="Efectivo">Efectivo</option>
            <option value="Transfer">Transfer</option>
            <option value="Bonificacion">Bonificacion</option>
          </select>
        </div>
        <div>
          {/* D-AUDIT-CXC (2026-09-28): V1 `AbonosCreate.jsx` (L512-521) filtra el
             <select> de Banco a solo cuentas MXN — corregido aquí, antes se
             listaban también cuentas USD que V1 nunca permitía elegir en Abonos. */}
          <label className="label">Banco</label>
          <select value={selectedBanco} onChange={(e) => setSelectedBanco(e.target.value)}
            className="select-base">
            <option value="">Selecciona un Banco</option>
            {[...bancos].sort((a, b) => (a.banco > b.banco ? 1 : -1)).filter((b) => b.moneda === 'MXN').map((b) => <option key={b._id} value={b._id}>{b.banco} {b.cuenta}</option>)}
          </select>
        </div>
        <div className="text-right">
          <div className="text-xs font-medium text-slate-500">Total Pago</div>
          <div className="text-lg font-semibold text-danger-600">{formatMoney(total)}</div>
        </div>
      </div>

      <div className="overflow-x-auto card">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2"></th>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Nota</th>
              <th className="px-3 py-2 text-right">Total MXN</th>
              <th className="px-3 py-2 text-right">Comision</th>
              <th className="px-3 py-2 text-right">Abonos</th>
              <th className="px-3 py-2 text-right">Saldo</th>
              <th className="px-3 py-2 text-right">Importe</th>
              <th className="px-3 py-2 text-left">Observaciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!selectedCliente && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">Selecciona un cliente</td></tr>
            )}
            {selectedCliente && renglones.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">Sin cargos pendientes</td></tr>
            )}
            {renglones.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2">
                  <input type="checkbox" checked={r.select} onChange={(e) => actualizarRenglon(r.id, { select: e.target.checked })} />
                </td>
                <td className="px-3 py-2">{formatDate(r.fecha)}</td>
                <td className="px-3 py-2">{r.nota}</td>
                <td className="px-3 py-2 text-right">{formatMoney(r.total)}</td>
                <td className="px-3 py-2 text-right">{formatMoney(r.comision)}</td>
                <td className="px-3 py-2 text-right">{formatMoney(r.abonos)}</td>
                <td className="px-3 py-2 text-right">{formatMoney(r.saldo)}</td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number" step="0.01" disabled={!r.select} value={r.importe}
                    onChange={(e) => cambiarImporte(r.id, e.target.value)}
                    className="input-base w-28 text-right py-1"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text" value={r.observaciones}
                    onChange={(e) => actualizarRenglon(r.id, { observaciones: e.target.value })}
                    className="input-base py-1"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={guardar} disabled={guardando}
          className="btn-primary">
          {guardando ? 'Registrando...' : 'Registrar'}
        </button>
        <Link to="/abonos" className="btn-outline">Regresar</Link>
      </div>

      {/* Modal de progreso de V1 ("Procesando / Por favor no cierre ni refresque su navegador.") */}
      {guardando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="rounded-xl bg-white p-8 text-center shadow-xl">
            <h3 className="text-xl font-semibold text-slate-800">Procesando</h3>
            <p className="mt-3 text-slate-500">Por favor no cierre ni refresque su navegador.</p>
            <p className="mt-4 text-sm italic text-slate-400">Este proceso puede tomar unos instantes...</p>
          </div>
        </div>
      )}
    </div>
  );
}
